/**
 * サーバの 409（COMPANY_CONTEXT_MISMATCH / STORE_CONTEXT_MISMATCH）を受けたときの読み直し（2026-10-17）。
 * 設計: docs/04_機能仕様/複数の会社に所属するアカウント設計.md §9.1
 *
 * 通信の共通処理（axios.ts）の応答インターセプタの**先頭**から呼ぶ（判定は companyContext.ts の classifyResponseError）。
 * 呼び出し元には解決しない Promise を返し、各画面の catch を走らせない（退勤の 409 は「すでに退勤済みです」と出すので、
 * ここで受けないと古いタブで退勤を押したキャストが「退勤済み」と思って帰る）。
 * ⚠️ axios をここで読み込まない（axios.ts から呼ぶので循環する）
 */
import { clearUnsaved } from './unsavedChanges';
import { getCurrentCompanyId, getCurrentMembership, lastStoreKey, storeToRestore } from './companyContext';

export { isContextMismatch } from './companyContext';

/** 読み直した後に出す理由（ContextReloadNotice が出して消す） */
export const CONTEXT_NOTICE_KEY = 'logme:context_notice';

export const COMPANY_MISMATCH_NOTICE = '別の画面でお店が切り替わりました。画面を読み直しました。';
export const STORE_MISMATCH_NOTICE = '選んでいたお店が使えなくなりました。お店を選び直してください。';
export const storeRestoredNotice = (storeName: string) => `選んでいたお店が使えなくなったので、前回のお店「${storeName}」に戻しました。`;

let reloading = false;

function saveNotice(message: string): void {
  try { sessionStorage.setItem(CONTEXT_NOTICE_KEY, message); } catch { /* 保存できなくても読み直す */ }
}

/**
 * 別のタブで会社が切り替わった。全体を読み直し、**今の会社での役割のホーム**へ。
 * 同じ URL だと、A では管理者・B ではキャストの人が管理画面の 403 の中に入る。このタブは新しい会社での役割を知らないので、
 * 行き先は /login（ログイン済みなら役割のホームへ送る処理に任せる）。
 * 移動の直前に clearUnsaved()（呼ばないとブラウザ標準の「離れますか」が出て、留まると 409 が繰り返される）
 */
export function onCompanyContextMismatch(): void {
  if (typeof window === 'undefined' || reloading) return;
  reloading = true;
  saveNotice(COMPANY_MISMATCH_NOTICE);
  clearUnsaved();
  window.location.href = '/login';
}

/**
 * 選んでいる店が別の会社のものになった（端末に古い店が残っていた・別の会社に切り替えた後の古い要求）。
 * 🔴 保存値を**消してから**読み直す（消さないと同じ店の ID でまた 409 になり、読み直しが止まらない）。
 * その端末で**その会社で**前回選んだ店が今の会社の店なら、それを書いてから読み直す
 * （書かないと StoreContext が先頭の店を選び、黒服が「2 号店」から「本店」へ黙って移る）
 */
export function onStoreContextMismatch(): void {
  if (typeof window === 'undefined' || reloading) return;
  reloading = true;
  let notice = STORE_MISMATCH_NOTICE;
  try {
    const rejected = localStorage.getItem('selected_store_id');
    localStorage.removeItem('selected_store_id');
    (window as any).__logmeSelectedStoreId = undefined;

    const userId = localStorage.getItem('current_user_id');
    const companyId = getCurrentCompanyId();
    const last = userId && companyId ? localStorage.getItem(lastStoreKey(userId, companyId)) : null;
    const restore = storeToRestore(rejected, last, getCurrentMembership());
    if (restore) {
      localStorage.setItem('selected_store_id', String(restore.id));
      notice = storeRestoredNotice(restore.name);
    }
  } catch { /* 読めなくても読み直す */ }
  saveNotice(notice);
  clearUnsaved();
  window.location.reload();
}

/** テスト用 */
export function resetContextMismatchForTest(): void {
  reloading = false;
}
